import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const CalorieCalculatorPage = () => {
  const [unitSystem, setUnitSystem] = useState('us');
  const [age, setAge] = useState('25');
  const [gender, setGender] = useState('male');
  const [heightFeet, setHeightFeet] = useState('5');
  const [heightInches, setHeightInches] = useState('10');
  const [heightCm, setHeightCm] = useState('178');
  const [weightLbs, setWeightLbs] = useState('160');
  const [weightKg, setWeightKg] = useState('73');
  const [activity, setActivity] = useState('1.55');
  const [result, setResult] = useState(null);

  const activityLevels = [
    { value: '1.2', label: 'Sedentary (little or no exercise)' },
    { value: '1.375', label: 'Light (exercise 1-3 days/week)' },
    { value: '1.55', label: 'Moderate (exercise 3-5 days/week)' },
    { value: '1.725', label: 'Active (exercise 6-7 days/week)' },
    { value: '1.9', label: 'Very Active (hard exercise daily)' }
  ];

  const calculate = () => {
    let heightCmVal, weightKgVal;

    if (unitSystem === 'us') {
      heightCmVal = (parseFloat(heightFeet) * 12 + parseFloat(heightInches)) * 2.54;
      weightKgVal = parseFloat(weightLbs) * 0.453592;
    } else {
      heightCmVal = parseFloat(heightCm);
      weightKgVal = parseFloat(weightKg);
    }

    const ageVal = parseFloat(age);
    const activityMultiplier = parseFloat(activity);

    let bmr;
    if (gender === 'male') {
      bmr = 10 * weightKgVal + 6.25 * heightCmVal - 5 * ageVal + 5;
    } else {
      bmr = 10 * weightKgVal + 6.25 * heightCmVal - 5 * ageVal - 161;
    }

    const maintenance = bmr * activityMultiplier;
    const mildLoss = maintenance - 250;
    const weightLoss = maintenance - 500;
    const extremeLoss = maintenance - 1000;
    const mildGain = maintenance + 250;
    const weightGain = maintenance + 500;

    setResult({
      bmr: Math.round(bmr),
      maintenance: Math.round(maintenance),
      mildLoss: Math.round(mildLoss),
      weightLoss: Math.round(weightLoss),
      extremeLoss: Math.round(extremeLoss),
      mildGain: Math.round(mildGain),
      weightGain: Math.round(weightGain)
    });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/fitness-health" className="text-cyan-400 hover:text-cyan-300">Fitness & Health</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Calorie Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Calorie Calculator</h1>
          <p className="text-gray-400 mb-6">
            This Calorie Calculator can be used to estimate the number of calories a person needs to consume each day. 
            This calculator can also provide some simple guidelines for gaining or losing weight.
          </p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex border-b border-[#2a2a2a] mb-6">
              <button onClick={() => setUnitSystem('us')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${unitSystem === 'us' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                US Units
              </button>
              <button onClick={() => setUnitSystem('metric')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${unitSystem === 'metric' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                Metric Units
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-20">Age</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={age} onChange={(e) => setAge(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                  <span className="text-gray-400 text-sm">years</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-20">Gender</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={gender === 'male'} onChange={() => setGender('male')}
                      className="w-4 h-4 accent-cyan-500" />
                    <span className="text-gray-300">Male</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={gender === 'female'} onChange={() => setGender('female')}
                      className="w-4 h-4 accent-cyan-500" />
                    <span className="text-gray-300">Female</span>
                  </label>
                </div>
              </div>

              {unitSystem === 'us' ? (
                <>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="text-gray-300 font-medium w-20">Height</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)}
                        className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">feet</span>
                      <Input type="number" value={heightInches} onChange={(e) => setHeightInches(e.target.value)}
                        className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">inches</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="text-gray-300 font-medium w-20">Weight</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">pounds</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="text-gray-300 font-medium w-20">Height</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">cm</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="text-gray-300 font-medium w-20">Weight</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">kg</span>
                    </div>
                  </div>
                </>
              )}

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-20">Activity</label>
                <select value={activity} onChange={(e) => setActivity(e.target.value)}
                  className="flex-1 max-w-md px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                  {activityLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">
              Calculate
            </Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-lg font-semibold text-white">Results</h3>
                </div>

                <div className="text-center mb-6">
                  <p className="text-gray-400 mb-2">Maintenance Calories</p>
                  <p className="text-4xl font-bold text-green-400">{result.maintenance.toLocaleString()}</p>
                  <p className="text-gray-500 text-sm">Calories/day</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="text-white font-semibold">Weight Loss</h4>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Mild (0.5 lb/week)</span>
                      <span className="text-yellow-400 font-semibold">{result.mildLoss.toLocaleString()} cal</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Weight Loss (1 lb/week)</span>
                      <span className="text-orange-400 font-semibold">{result.weightLoss.toLocaleString()} cal</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Extreme (2 lb/week)</span>
                      <span className="text-red-400 font-semibold">{result.extremeLoss.toLocaleString()} cal</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-white font-semibold">Weight Gain</h4>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Mild (0.5 lb/week)</span>
                      <span className="text-cyan-400 font-semibold">{result.mildGain.toLocaleString()} cal</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Weight Gain (1 lb/week)</span>
                      <span className="text-blue-400 font-semibold">{result.weightGain.toLocaleString()} cal</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">BMR (Basal Metabolic Rate)</span>
                      <span className="text-gray-300 font-semibold">{result.bmr.toLocaleString()} cal</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/bmi-calculator" className="text-cyan-400 hover:text-cyan-300">BMI Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/bmr-calculator" className="text-cyan-400 hover:text-cyan-300">BMR Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/body-fat-calculator" className="text-cyan-400 hover:text-cyan-300">Body Fat Calculator</Link>
          </div>

          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">How Calories Work</h2>
              <p className="text-gray-400 leading-relaxed">
                Calories are a measure of energy. The calorie content of foods is a measure of the potential energy it holds. 
                The body uses calories from food to fuel functions like breathing, circulation, and physical activity.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Mifflin-St Jeor Equation</h2>
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                <p className="text-gray-300 font-mono text-sm mb-2">For men: BMR = 10W + 6.25H - 5A + 5</p>
                <p className="text-gray-300 font-mono text-sm mb-2">For women: BMR = 10W + 6.25H - 5A - 161</p>
                <p className="text-gray-500 text-xs">W = weight in kg, H = height in cm, A = age in years</p>
              </div>
            </section>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Fitness & Health Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'BMI', path: '/bmi-calculator' },
                { name: 'Calorie', path: '/calorie-calculator' },
                { name: 'BMR', path: '/bmr-calculator' },
                { name: 'Body Fat', path: '/body-fat-calculator' },
                { name: 'Ideal Weight', path: '/ideal-weight-calculator' },
                { name: 'Pace', path: '/pace-calculator' },
              ].map((calc) => (
                <Link key={calc.path} to={calc.path}
                  className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 text-sm transition-colors">
                  {calc.name}
                </Link>
              ))}
            </div>
          </div>

          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CalorieCalculatorPage;
